import type { Entity, Link, SourceRef } from '@/types/entity';
import type { Project } from '@/types/project';
import { APP_VERSION, DB_NAME } from '@/lib/constants';

export const CANON_BUNDLE_SCHEMA_VERSION = 'codex-engine-canon-bundle/v1' as const;

export interface CanonBundleV1 {
  schemaVersion: typeof CANON_BUNDLE_SCHEMA_VERSION;
  project: CanonProjectRef;
  entities: CanonEntityRef[];
  links: CanonLinkRef[];
  worldProjection: EngineWorldDTO;
  beatSequences: EngineBeatSeqDTO[];
  assertionCandidates: AssertionCandidate[];
  provenance: BundleProvenance;
  indexScopes: IndexScopeRef[];
}

export interface CanonProjectRef {
  id: string;
  title: string;
  schemaVersion: number;
  exportedAt: number;
}

export interface CanonEntityRef {
  id: string;
  type: string;
  name: string;
  canonStatus: string;
  confidence: string;
  sourceRefs: SourceRef[];
  version: number;
  fields: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

export interface CanonLinkRef {
  id: string;
  from: string;
  to: string;
  type: string;
  label?: string;
  directed: boolean;
  canonStatus: string;
  confidence: string;
  version: number;
}

export interface BundleProvenance {
  source: 'codex';
  codexDbName: string;
  codexAppVersion?: string;
  generatedBy: 'codex-engine-adapter';
  generatedAt: number;
}

export interface IndexScopeRef {
  scope: 'canon' | 'draft' | 'proposal';
  entityIds: string[];
  beatIds: string[];
}

export interface EngineWorldDTO {
  world: string;
  time_scopes: Record<string, EngineTimeScopeDTO>;
  actors: EngineActorDTO[];
  institutions: EngineInstitutionDTO[];
  resources: EngineResourceFlowDTO[];
  locations: EngineLocationDTO[];
  constraints: EngineConstraintDTO[];
  causality: EngineCausalLinkDTO[];
}

export interface EngineTimeScopeDTO {
  label: string;
  order: number; // ★봉인 ordinal(plot 컬럼 순서) — 엔진 facts_up_to가 사전식 키 대신 이걸로 미래 part 차단
  facts: string[];
  lawless?: boolean;
}

export interface EngineActorDTO {
  id: string;
  status?: 'proposed';
  role?: string;
  arc?: string;
  arc_constraint?: string;
}

export interface EngineInstitutionDTO {
  id: string;
  /** 'proposed' = canon 미확정 마커(우선) · 그 외 = 운영 상태(collapsed 등, C6가 읽음 — K5-c) */
  status?: string;
  kind?: string;
  controls: string[];
  economics?: string;
  lawful: boolean;
  sustained_by?: string;
  sustained_by_institution?: string;
}

export interface EngineResourceFlowDTO {
  id: string;
  status?: 'proposed';
  flow?: string;
  fragility?: string;
  collapses_in: string[];
}

export interface EngineLocationDTO {
  id: string;
  status?: 'proposed';
  kind?: string;
}

export interface EngineConstraintDTO {
  id: string;
  type: 'fact' | 'timeline' | 'causality' | 'macro-system' | 'character' | 'tone';
  rule: string;
  check: 'deterministic' | 'llm_proposed';
}

export interface EngineCausalLinkDTO {
  id: string;
  effect: string;
  requires: string[];
}

export interface EngineBeatSeqDTO {
  sequence: string;
  time_scope: string;
  source: 'codex';
  beats: EngineBeatDTO[];
}

export interface EngineBeatDTO {
  id: string;
  title: string;
  body: string;
  refs: string[];
  asserts: EngineAssertDTO[];
}

export interface EngineAssertDTO {
  claim: string;
  references_fact?: string;
  references_entity?: string;
}

export interface AssertionCandidate {
  id: string;
  beatId: string;
  claim: string;
  references_fact?: string;
  references_entity?: string;
  defines_fact?: string;
  status: 'pending' | 'accepted' | 'rejected';
  source: 'rule' | 'llm' | 'manual';
  confidence: number;
  rationale: string;
  evidence: {
    bodyExcerpt?: string;
    entityIds: string[];
    canonRefs: string[];
  };
  createdAt: number;
  decidedAt?: number;
}

export interface EngineRunReport {
  id: string;
  bundleSchemaVersion: typeof CANON_BUNDLE_SCHEMA_VERSION;
  projectId: string;
  status: 'ready' | 'error';
  createdAt: number;
  engine: {
    source: 'loom-engine';
    version?: string;
    mode: 'deterministic' | 'deterministic+llm';
  };
  input: {
    worldHash: string;
    beatSeqHashes: string[];
    selectedBeatIds: string[];
  };
  summary: {
    violationCount: number;
    hardBefore: number;
    hardAfter?: number;
    appliedProposalCount?: number;
  };
  violations: EngineViolationDTO[];
  proposals: EngineProposalDTO[];
  error?: string;
}

export interface EngineViolationDTO {
  violation_id: string;
  beat: string;
  rule: string;
  kind: string;
  check: 'deterministic' | 'llm_proposed';
  severity: string;
  evidence: string;
  confidence: number;
  provenance: {
    source: string;
    prompt_hash?: string;
  };
  subject?: string;
  canon_refs: string[];
  hard: boolean;
}

export interface EngineProposalDTO {
  id: string;
  beat: string;
  kind: string;
  evidence: string;
  patch: { type: 'RemoveAssert'; claim: string } | { type: 'NeedsRewrite'; note: string };
  auto_applicable: boolean;
  status: 'proposed' | 'accepted' | 'rejected';
}

export interface EngineBeatPressure {
  beatId: string;
  acceptedAssertions: number;
  pendingAssertions: number;
  rejectedAssertions: number;
  hardViolations: number;
  unchecked: boolean;
  proposedPatches: number;
  latestRunAt?: number;
}

export interface EnginePressure {
  summary: {
    hardViolations: number;
    uncheckedBeats: number;
    pendingAssertionCandidates: number;
    proposedPatches: number;
  };
  byBeat: Record<string, EngineBeatPressure>;
}

export interface BuildCanonBundleInput {
  project: Project;
  entities: Entity[];
  links: Link[];
  generatedAt?: number;
  codexDbName?: string;
  appVersion?: string;
}

type EngineMeta = Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0) : [];
}

function engineMeta(value: { meta?: Record<string, unknown> }): EngineMeta {
  const meta = value.meta;
  if (!isRecord(meta)) return {};
  const engine = meta.engine;
  return isRecord(engine) ? engine : {};
}

function projectEngineMeta(project: Project): EngineMeta {
  const engine = project.meta.engine;
  return isRecord(engine) ? engine : {};
}

function activeEntity(entity: Entity): boolean {
  return !entity.deletedAt;
}

function activeLink(link: Link): boolean {
  return !link.deletedAt;
}

function isCanonConfirmed(entity: Pick<Entity, 'canonStatus' | 'confidence'>): boolean {
  return entity.canonStatus === 'canon' && entity.confidence === 'confirmed';
}

function proposedStatus(entity: Pick<Entity, 'canonStatus' | 'confidence'>): 'proposed' | undefined {
  return isCanonConfirmed(entity) ? undefined : 'proposed';
}

function optionalStringField(record: Record<string, unknown>, key: string): string | undefined {
  return stringValue(record[key]);
}

function characterConstraint(fields: Record<string, unknown>): string | undefined {
  const keys = ['wound', 'lie', 'desire', 'need', 'flaw', 'arc', 'line'];
  const parts = keys
    .map((key) => {
      const value = stringValue(fields[key]);
      return value ? `${key}: ${value}` : undefined;
    })
    .filter((part): part is string => Boolean(part));
  return parts.length ? parts.join(' | ') : undefined;
}

function buildTimeScopes(project: Project, entities: Entity[]): Record<string, EngineTimeScopeDTO> {
  const engine = projectEngineMeta(project);
  const configured = isRecord(engine.timeScopes) ? engine.timeScopes : {};
  const scopes: Record<string, EngineTimeScopeDTO> = {};
  for (const column of project.plotColumns.slice().sort((a, b) => a.order - b.order)) {
    const scopeMeta = configured[column.id];
    const meta: Record<string, unknown> = isRecord(scopeMeta) ? scopeMeta : {};
    scopes[column.id] = {
      label: column.label,
      order: column.order, // 봉인 ordinal — 사전식 scope명(setup/rising/climax)에서도 미래 part 안 새게
      facts: stringArray(meta.facts),
      ...(meta.lawless === true ? { lawless: true } : {}),
    };
  }
  for (const beat of entities.filter((entity) => entity.type === 'beat')) {
    const scope = stringValue(beat.fields.col) ?? project.plotColumns[0]?.id;
    if (!scope || !scopes[scope]) continue;
    const facts = new Set(scopes[scope].facts);
    for (const candidate of getAssertionCandidates(beat)) {
      if (candidate.status !== 'accepted') continue;
      const defined = stringValue(candidate.defines_fact);
      if (defined) facts.add(defined);
    }
    scopes[scope].facts = [...facts];
  }
  return scopes;
}

const CONSTRAINT_TYPES = ['fact', 'timeline', 'causality', 'macro-system', 'character', 'tone'] as const;

// 프레임워크 기본 = 법칙 *종류*만 제공(특정 세계 콘텐츠 아님). 작가가 project.meta.engine.constraints로
// 같은 id를 덮어쓰거나 새 제약을 선언하면 그것이 우선한다("프레임워크지 콘텐츠 아님" 원칙).
function buildConstraints(project: Project): EngineConstraintDTO[] {
  const defaults: EngineConstraintDTO[] = [
    { id: 'C3', type: 'fact', rule: '무법 지대(lawful=false 제도)에서 합법 공권력/제도 보호 전제 금지', check: 'deterministic' },
    { id: 'C1', type: 'timeline', rule: '비트 scope와 fact scope 일치', check: 'deterministic' },
    { id: 'C4', type: 'causality', rule: 'effect fact는 선행 인과 requires를 필요로 함', check: 'deterministic' },
    { id: 'C6', type: 'macro-system', rule: '자원-제도-제도 거시 의존 연쇄 정합성', check: 'deterministic' },
  ];
  const engine = projectEngineMeta(project);
  if (!Array.isArray(engine.constraints)) return defaults;
  const byId = new Map<string, EngineConstraintDTO>(defaults.map((c) => [c.id, c]));
  for (const c of engine.constraints) {
    if (!isRecord(c)) continue;
    const id = stringValue(c.id);
    const type = stringValue(c.type);
    const rule = stringValue(c.rule);
    if (!id || !rule || !type || !CONSTRAINT_TYPES.includes(type as (typeof CONSTRAINT_TYPES)[number])) continue;
    const check = stringValue(c.check) === 'llm_proposed' ? 'llm_proposed' : 'deterministic';
    byId.set(id, { id, type: type as EngineConstraintDTO['type'], rule, check });
  }
  return [...byId.values()];
}

function buildCausality(project: Project): EngineCausalLinkDTO[] {
  const engine = projectEngineMeta(project);
  if (!Array.isArray(engine.causality)) return [];
  return engine.causality.flatMap((item) => {
    if (!isRecord(item)) return [];
    const id = stringValue(item.id);
    const effect = stringValue(item.effect);
    const requires = stringArray(item.requires);
    return id && effect ? [{ id, effect, requires }] : [];
  });
}

function toWorldProjection(project: Project, entities: Entity[]): EngineWorldDTO {
  const actors: EngineActorDTO[] = [];
  const institutions: EngineInstitutionDTO[] = [];
  const resources: EngineResourceFlowDTO[] = [];
  const locations: EngineLocationDTO[] = [];

  for (const entity of entities) {
    const fields = entity.fields;
    const engine = engineMeta(entity);
    const status = proposedStatus(entity);
    if (entity.type === 'character') {
      actors.push({
        id: entity.id,
        ...(status ? { status } : {}),
        ...(optionalStringField(fields, 'role') ? { role: optionalStringField(fields, 'role') } : {}),
        ...(optionalStringField(fields, 'arc') ? { arc: optionalStringField(fields, 'arc') } : {}),
        ...(characterConstraint(fields) ? { arc_constraint: characterConstraint(fields) } : {}),
      });
    } else if (entity.type === 'faction') {
      const opStatus = stringValue(engine.status);
      institutions.push({
        id: entity.id,
        ...(status ? { status } : opStatus ? { status: opStatus } : {}),
        ...(optionalStringField(fields, 'alignment') || optionalStringField(fields, 'desc')
          ? { kind: optionalStringField(fields, 'alignment') ?? optionalStringField(fields, 'desc') }
          : {}),
        controls: stringArray(engine.controls),
        ...(stringValue(engine.economics) ?? optionalStringField(fields, 'goal')
          ? { economics: stringValue(engine.economics) ?? optionalStringField(fields, 'goal') }
          : {}),
        lawful: engine.lawful === true,
        ...(stringValue(engine.sustained_by) ? { sustained_by: stringValue(engine.sustained_by) } : {}),
        ...(stringValue(engine.sustained_by_institution)
          ? { sustained_by_institution: stringValue(engine.sustained_by_institution) }
          : {}),
      });
    } else if (entity.type === 'planet' || entity.type === 'location') {
      locations.push({
        id: entity.id,
        ...(status ? { status } : {}),
        ...(optionalStringField(fields, entity.type === 'planet' ? 'tag' : 'kind')
          ? { kind: optionalStringField(fields, entity.type === 'planet' ? 'tag' : 'kind') }
          : {}),
      });
    }

    if (engine.kind === 'resource_flow') {
      resources.push({
        id: entity.id,
        ...(status ? { status } : {}),
        ...(stringValue(engine.flow) ? { flow: stringValue(engine.flow) } : {}),
        ...(stringValue(engine.fragility) ? { fragility: stringValue(engine.fragility) } : {}),
        collapses_in: stringArray(engine.collapses_in),
      });
    }
  }

  return {
    world: project.id,
    time_scopes: buildTimeScopes(project, entities),
    actors,
    institutions,
    resources,
    locations,
    constraints: buildConstraints(project),
    causality: buildCausality(project),
  };
}

export function getAssertionCandidates(entity: Entity): AssertionCandidate[] {
  const engine = engineMeta(entity);
  return Array.isArray(engine.assertions) ? (engine.assertions as AssertionCandidate[]) : [];
}

export function toEngineAsserts(beat: Entity): EngineAssertDTO[] {
  return getAssertionCandidates(beat)
    .filter((candidate) => candidate.status === 'accepted')
    .map((candidate) => ({
      claim: candidate.claim,
      ...(candidate.references_fact ? { references_fact: candidate.references_fact } : {}),
      ...(candidate.references_entity ? { references_entity: candidate.references_entity } : {}),
    }));
}

function collectBeatRefs(beat: Entity, activeIds: Set<string>): string[] {
  const refs = new Set<string>();
  const add = (value: unknown) => {
    if (typeof value === 'string' && activeIds.has(value)) refs.add(value);
  };
  for (const ref of beat.refs ?? []) add(ref);
  for (const ref of stringArray(beat.fields.characters)) add(ref);
  add(beat.fields.location);
  // storylineId는 엔진 refs에서 제외(K0 발견): 스토리라인은 플롯 *조직* 단위지 세계 진실 엔티티가 아니라
  // worldProjection에 투영되지 않는다 — refs에 실으면 전 비트가 existence 위반(거짓 hard)을 뒤집어쓴다.
  return [...refs];
}

function buildBeatSequences(project: Project, entities: Entity[]): EngineBeatSeqDTO[] {
  const activeIds = new Set(entities.map((entity) => entity.id));
  const plotOrder = new Map(project.plotColumns.map((column) => [column.id, column.order]));
  const beats = entities
    .filter((entity) => entity.type === 'beat')
    .slice()
    .sort((a, b) => {
      const colA = String(a.fields.col ?? '');
      const colB = String(b.fields.col ?? '');
      const colDiff = (plotOrder.get(colA) ?? Number.MAX_SAFE_INTEGER) - (plotOrder.get(colB) ?? Number.MAX_SAFE_INTEGER);
      if (colDiff !== 0) return colDiff;
      return Number(a.fields.order ?? 0) - Number(b.fields.order ?? 0);
    });
  const byScope = new Map<string, Entity[]>();
  for (const beat of beats) {
    const scope = stringValue(beat.fields.col) ?? project.plotColumns[0]?.id ?? 'default';
    byScope.set(scope, [...(byScope.get(scope) ?? []), beat]);
  }
  return [...byScope.entries()].map(([scope, scopedBeats]) => ({
    sequence: `codex-${scope}`,
    time_scope: scope,
    source: 'codex',
    beats: scopedBeats.map((beat) => ({
      id: beat.id,
      title: beat.name,
      body: String(beat.fields.bodyDraft ?? ''),
      refs: collectBeatRefs(beat, activeIds),
      asserts: toEngineAsserts(beat),
    })),
  }));
}

function buildIndexScopes(entities: Entity[], assertionCandidates: AssertionCandidate[]): IndexScopeRef[] {
  const canonEntities = entities.filter(isCanonConfirmed).map((entity) => entity.id);
  const draftEntities = entities.filter((entity) => !isCanonConfirmed(entity)).map((entity) => entity.id);
  const canonBeats = entities.filter((entity) => entity.type === 'beat' && isCanonConfirmed(entity)).map((entity) => entity.id);
  const draftBeats = entities.filter((entity) => entity.type === 'beat' && !isCanonConfirmed(entity)).map((entity) => entity.id);
  return [
    { scope: 'canon', entityIds: canonEntities, beatIds: canonBeats },
    { scope: 'draft', entityIds: draftEntities, beatIds: draftBeats },
    {
      scope: 'proposal',
      entityIds: [],
      beatIds: [...new Set(assertionCandidates.filter((candidate) => candidate.status === 'pending').map((candidate) => candidate.beatId))],
    },
  ];
}

export function buildCanonBundle(input: BuildCanonBundleInput): CanonBundleV1 {
  const generatedAt = input.generatedAt ?? Date.now();
  const entities = input.entities.filter(activeEntity);
  const activeIds = new Set(entities.map((entity) => entity.id));
  const links = input.links.filter((link) => activeLink(link) && activeIds.has(link.from) && activeIds.has(link.to));
  const assertionCandidates = entities.flatMap((entity) => (entity.type === 'beat' ? getAssertionCandidates(entity) : []));

  return {
    schemaVersion: CANON_BUNDLE_SCHEMA_VERSION,
    project: {
      id: input.project.id,
      title: input.project.title,
      schemaVersion: input.project.schemaVersion,
      exportedAt: generatedAt,
    },
    entities: entities.map((entity) => ({
      id: entity.id,
      type: entity.type,
      name: entity.name,
      canonStatus: entity.canonStatus,
      confidence: entity.confidence,
      sourceRefs: entity.sourceRefs ?? [],
      version: entity.version,
      fields: entity.fields,
      ...(entity.meta ? { meta: entity.meta } : {}),
    })),
    links: links.map((link) => ({
      id: link.id,
      from: link.from,
      to: link.to,
      type: link.type,
      ...(link.label ? { label: link.label } : {}),
      directed: link.directed,
      canonStatus: link.canonStatus,
      confidence: link.confidence,
      version: link.version,
    })),
    worldProjection: toWorldProjection(input.project, entities),
    beatSequences: buildBeatSequences(input.project, entities),
    assertionCandidates,
    provenance: {
      source: 'codex',
      codexDbName: input.codexDbName ?? DB_NAME,
      codexAppVersion: input.appVersion ?? APP_VERSION,
      generatedBy: 'codex-engine-adapter',
      generatedAt,
    },
    indexScopes: buildIndexScopes(entities, assertionCandidates),
  };
}

export function createAssertionCandidate(input: {
  id: string;
  beatId: string;
  claim: string;
  references_fact?: string;
  references_entity?: string;
  defines_fact?: string;
  source: AssertionCandidate['source'];
  confidence?: number;
  rationale?: string;
  evidence?: Partial<AssertionCandidate['evidence']>;
  createdAt?: number;
}): AssertionCandidate {
  return {
    id: input.id,
    beatId: input.beatId,
    claim: input.claim,
    ...(input.references_fact ? { references_fact: input.references_fact } : {}),
    ...(input.references_entity ? { references_entity: input.references_entity } : {}),
    ...(input.defines_fact ? { defines_fact: input.defines_fact } : {}),
    status: 'pending',
    source: input.source,
    confidence: input.confidence ?? 0.8,
    rationale: input.rationale ?? '',
    evidence: {
      bodyExcerpt: input.evidence?.bodyExcerpt,
      entityIds: input.evidence?.entityIds ?? [],
      canonRefs: input.evidence?.canonRefs ?? [],
    },
    createdAt: input.createdAt ?? Date.now(),
  };
}

function withAssertions(beat: Entity, assertions: AssertionCandidate[], updatedAt: number): Entity {
  const meta = isRecord(beat.meta) ? beat.meta : {};
  const engine = isRecord(meta.engine) ? meta.engine : {};
  return {
    ...beat,
    meta: { ...meta, engine: { ...engine, assertions } },
    version: beat.version + 1,
    updatedAt,
  };
}

export function setAssertionStatus(
  beat: Entity,
  assertionId: string,
  status: AssertionCandidate['status'],
  decidedAt = Date.now(),
): Entity {
  const assertions = getAssertionCandidates(beat).map((candidate) =>
    candidate.id === assertionId ? { ...candidate, status, decidedAt } : candidate,
  );
  return withAssertions(beat, assertions, decidedAt);
}

export function applyRemoveAssertProposal(beat: Entity, claim: string, decidedAt = Date.now()): Entity {
  const assertions = getAssertionCandidates(beat).map((candidate) =>
    candidate.status === 'accepted' && candidate.claim === claim
      ? { ...candidate, status: 'rejected' as const, decidedAt }
      : candidate,
  );
  return withAssertions(beat, assertions, decidedAt);
}

function emptyBeatPressure(beatId: string): EngineBeatPressure {
  return {
    beatId,
    acceptedAssertions: 0,
    pendingAssertions: 0,
    rejectedAssertions: 0,
    hardViolations: 0,
    unchecked: false,
    proposedPatches: 0,
  };
}

export function deriveEnginePressure(entities: Entity[], reports: EngineRunReport[] = []): EnginePressure {
  const byBeat: Record<string, EngineBeatPressure> = {};
  const ensure = (beatId: string) => (byBeat[beatId] ??= emptyBeatPressure(beatId));

  for (const entity of entities.filter((item) => item.type === 'beat' && !item.deletedAt)) {
    const beat = ensure(entity.id);
    const assertions = getAssertionCandidates(entity);
    beat.acceptedAssertions = assertions.filter((candidate) => candidate.status === 'accepted').length;
    beat.pendingAssertions = assertions.filter((candidate) => candidate.status === 'pending').length;
    beat.rejectedAssertions = assertions.filter((candidate) => candidate.status === 'rejected').length;
    if (String(entity.fields.bodyDraft ?? '').trim() && beat.acceptedAssertions === 0) {
      beat.unchecked = true;
    }
  }

  for (const report of reports) {
    for (const beatId of report.input.selectedBeatIds) {
      const beat = ensure(beatId);
      beat.latestRunAt = Math.max(beat.latestRunAt ?? 0, report.createdAt);
    }
    for (const violation of report.violations) {
      const beat = ensure(violation.beat);
      beat.latestRunAt = Math.max(beat.latestRunAt ?? 0, report.createdAt);
      if (violation.hard) beat.hardViolations += 1;
      if (violation.kind === 'unchecked') beat.unchecked = true;
    }
    for (const proposal of report.proposals) {
      const beat = ensure(proposal.beat);
      beat.latestRunAt = Math.max(beat.latestRunAt ?? 0, report.createdAt);
      if (proposal.status === 'proposed') beat.proposedPatches += 1;
    }
  }

  return {
    summary: {
      hardViolations: Object.values(byBeat).reduce((sum, beat) => sum + beat.hardViolations, 0),
      uncheckedBeats: Object.values(byBeat).filter((beat) => beat.unchecked).length,
      pendingAssertionCandidates: Object.values(byBeat).reduce((sum, beat) => sum + beat.pendingAssertions, 0),
      proposedPatches: Object.values(byBeat).reduce((sum, beat) => sum + beat.proposedPatches, 0),
    },
    byBeat,
  };
}
